import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Stack,
  Chip,
  IconButton,
  Collapse,
  Avatar,
  Divider,
} from '@mui/material';
import {
  ExpandMore,
  ExpandLess,
  CheckCircle,
  Person,
  CalendarToday,
  Description,
} from '@mui/icons-material';

/**
 * Collapsible Approval Stamp Component
 * Starts minimized, expands to show full approval details
 */
const ApprovalStampDrawer = ({ approvalInfo, invoice }) => {
  const [expanded, setExpanded] = useState(false);

  if (!approvalInfo) return null;

  const handleToggle = () => {
    setExpanded(!expanded);
  };

  const approvalDate = approvalInfo.date_confirmed 
    ? new Date(approvalInfo.date_confirmed).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : 'N/A';

  const approverName = approvalInfo.approved_by_name || 'System';
  const approverEmail = approvalInfo.approved_by_email || '';
  const reasoning = approvalInfo.Reasoning || approvalInfo.reasoning || 'No additional notes provided';

  return (
    <Card 
      sx={{ 
        borderRadius: 3,
        border: '2px solid',
        borderColor: 'success.main',
        bgcolor: 'success.50',
        overflow: 'hidden',
        boxShadow: 3,
      }}
    >
      {/* COLLAPSED VIEW - Always visible */}
      <CardContent 
        sx={{ 
          p: 2,
          cursor: 'pointer',
          '&:hover': {
            bgcolor: 'success.100',
          },
          transition: 'background-color 0.2s',
        }}
        onClick={handleToggle}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" alignItems="center" spacing={2}>
            <Avatar sx={{ bgcolor: 'success.main' }}>
              <CheckCircle />
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight={700} color="success.main">
                Approval Status: Complete
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Click to {expanded ? 'hide' : 'view'} approval details
              </Typography>
            </Box>
          </Stack>
          <IconButton>
            {expanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </Stack>
      </CardContent>

      {/* EXPANDED VIEW - Shows full approval details */}
      <Collapse in={expanded}>
        <Divider />
        <CardContent sx={{ p: 3, bgcolor: 'white' }}>
          <Stack spacing={3}>
            {/* Approval Header */}
            <Box sx={{ textAlign: 'center' }}>
              <Chip 
                label="APPROVED" 
                color="success" 
                sx={{ 
                  fontSize: '1.1rem', 
                  fontWeight: 700,
                  px: 3,
                  py: 2,
                  height: 'auto',
                }}
              />
            </Box>

            {/* Approval Details Grid */}
            <Stack spacing={2}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 2,
                p: 2,
                bgcolor: '#f8fafc',
                borderRadius: 2,
              }}>
                <Avatar sx={{ bgcolor: 'primary.main' }}>
                  <Person />
                </Avatar>
                <Box>
                  <Typography variant="caption" color="textSecondary" fontWeight={600}>
                    Approved By
                  </Typography>
                  <Typography variant="body1" fontWeight={700}>
                    {approverName}
                  </Typography>
                  {approverEmail && (
                    <Typography variant="caption" color="textSecondary">
                      {approverEmail}
                    </Typography>
                  )}
                </Box>
              </Box>

              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 2,
                p: 2,
                bgcolor: '#f8fafc',
                borderRadius: 2,
              }}>
                <Avatar sx={{ bgcolor: 'info.main' }}>
                  <CalendarToday />
                </Avatar>
                <Box>
                  <Typography variant="caption" color="textSecondary" fontWeight={600}>
                    Approval Date
                  </Typography>
                  <Typography variant="body1" fontWeight={700}>
                    {approvalDate}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ 
                display: 'flex', 
                alignItems: 'flex-start', 
                gap: 2,
                p: 2,
                bgcolor: '#f8fafc',
                borderRadius: 2,
              }}>
                <Avatar sx={{ bgcolor: 'secondary.main' }}>
                  <Description />
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" color="textSecondary" fontWeight={600}>
                    Approval Notes
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
                    {reasoning}
                  </Typography>
                </Box>
              </Box>
            </Stack>

            {/* Invoice Reference */}
            <Box sx={{ 
              mt: 2, 
              p: 2, 
              bgcolor: '#fef3c7', 
              borderRadius: 2,
              borderLeft: '4px solid #f59e0b'
            }}>
              <Typography variant="caption" color="textSecondary" fontWeight={600}>
                Invoice Reference
              </Typography>
              <Typography variant="body2" fontWeight={700}>
                {invoice?.po_number || 'N/A'}
              </Typography>
            </Box>
          </Stack>
        </CardContent>
      </Collapse>
    </Card>
  );
};

export default ApprovalStampDrawer;